import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Building, BuildingDocument, BuildingNote, BuildingStatus, Meter, MeterType, MeterLogResponse } from '../types';
import buildingService from '../services/buildingService';
import energyService from '../services/energyService';
import { useAuth } from '../contexts/AuthContext';
import { STATUS_CONFIG } from './Buildings';
import PdfPreview from '../components/PdfPreview';

const METER_TYPE_CONFIG: Record<MeterType, { label: string; icon: React.ReactNode; unit: string; color: string }> = {
  STROM: {
    label: 'Strom',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
    unit: 'kWh',
    color: 'text-[#216dab] dark:text-[#3aa6e9]',
  },
  GAS: {
    label: 'Gas',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.99 7.99 0 0120 13a7.99 7.99 0 01-2.343 5.657z" /></svg>,
    unit: 'm³',
    color: 'text-orange-600 dark:text-orange-400',
  },
  WASSER: {
    label: 'Wasser',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13c0 5-3.5 7.5-7.667 7.5S4.667 18 4.667 13C4.667 8.5 8.5 3 12.333 3s7.667 5.5 7.667 10z" /></svg>,
    unit: 'm³',
    color: 'text-cyan-600 dark:text-cyan-400',
  },
  PV: {
    label: 'PV',
    icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 9H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
    unit: 'kWh',
    color: 'text-amber-600 dark:text-amber-400',
  },
};

const inputCls = 'w-full px-4 py-2 bg-white dark:bg-slate-900 border border-black/10 dark:border-white/10 rounded-xl text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-colors placeholder-slate-400';
const labelCls = 'text-sm font-medium text-slate-500 px-1';

const BuildingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAdmin, user } = useAuth();

  const [building, setBuilding] = useState<Building | null>(null);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [loading, setLoading] = useState(true);

  // Building edit
  const [showBuildingEdit, setShowBuildingEdit] = useState(false);
  const [buildingFormData, setBuildingFormData] = useState({
    name: '', address: '', hasPv: false, active: true,
    contactName: '', contactPhone: '', contactEmail: '',
    status: 'NEU' as BuildingStatus, statusChangedAt: '',
  });

  // Meter create form
  const [showMeterForm, setShowMeterForm] = useState(false);
  const [meterFormData, setMeterFormData] = useState({
    type: 'STROM' as MeterType,
    name: '',
    meterNumber: '',
    consumptionPointNumber: '',
    energySupplier: '',
  });

  // Meter edit modal
  const [editingMeter, setEditingMeter] = useState<Meter | null>(null);
  const [meterEditData, setMeterEditData] = useState({
    name: '',
    meterNumber: '',
    consumptionPointNumber: '',
    energySupplier: '',
    currentReading: '',
  });

  // Notes state
  const [notes, setNotes] = useState<BuildingNote[]>([]);
  const [noteText, setNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  // Documents state
  const [documents, setDocuments] = useState<BuildingDocument[]>([]);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docName, setDocName] = useState('');
  const [docCategory, setDocCategory] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ doc: BuildingDocument; url: string; fileName: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Log section state
  const [selectedLogMeterId, setSelectedLogMeterId] = useState<number | null>(null);
  const [meterLog, setMeterLog] = useState<MeterLogResponse | null>(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const [deleteReadingsMeter, setDeleteReadingsMeter] = useState<Meter | null>(null);
  const [deleteAlsoCurrentReading, setDeleteAlsoCurrentReading] = useState(false);
  const [deletingReadings, setDeletingReadings] = useState(false);

  const buildingId = id ? parseInt(id, 10) : null;

  useEffect(() => { if (buildingId) loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    if (!buildingId) return;
    try {
      const [b, m, n, d] = await Promise.all([
        buildingService.getById(buildingId),
        buildingService.getMeters(buildingId),
        buildingService.getNotes(buildingId),
        buildingService.getDocuments(buildingId),
      ]);
      setBuilding(b);
      setMeters(m);
      setNotes(n);
      setDocuments(d);
    } catch { toast.error('Fehler beim Laden'); } finally { setLoading(false); }
  };

  const loadMeterLog = useCallback(async (meterId: number) => {
    setLoadingLog(true);
    setMeterLog(null);
    try {
      const log = await energyService.getMeterLog(meterId, 50);
      setMeterLog(log);
    } catch {
      toast.error('Fehler beim Laden der Log-Daten');
    } finally {
      setLoadingLog(false);
    }
  }, []);

  useEffect(() => {
    if (selectedLogMeterId !== null) {
      loadMeterLog(selectedLogMeterId);
    } else {
      setMeterLog(null);
    }
  }, [selectedLogMeterId, loadMeterLog]);

  // Building edit handlers
  const handleEditBuilding = () => {
    if (!building) return;
    setBuildingFormData({
      name: building.name,
      address: building.address ?? '',
      hasPv: building.hasPv,
      active: building.active,
      contactName: building.contactName ?? '',
      contactPhone: building.contactPhone ?? '',
      contactEmail: building.contactEmail ?? '',
      status: (building.status ?? 'NEU') as BuildingStatus,
      statusChangedAt: building.statusChangedAt ?? '',
    });
    setShowBuildingEdit(true);
  };

  const handleUpdateBuilding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!building) return;
    try {
      await buildingService.update(building.id, {
        ...buildingFormData,
        address: buildingFormData.address || undefined,
        contactName: buildingFormData.contactName.trim() || undefined,
        contactPhone: buildingFormData.contactPhone.trim() || undefined,
        contactEmail: buildingFormData.contactEmail.trim() || undefined,
        statusChangedAt: buildingFormData.statusChangedAt || undefined,
      });
      toast.success('Gebäude aktualisiert');
      setShowBuildingEdit(false);
      loadData();
    } catch { toast.error('Fehler'); }
  };

  // Meter CRUD handlers
  const resetMeterForm = () => {
    setMeterFormData({ type: 'STROM', name: '', meterNumber: '', consumptionPointNumber: '', energySupplier: '' });
    setShowMeterForm(false);
  };

  const handleCreateMeter = async () => {
    if (!buildingId) return;
    try {
      await buildingService.createMeter(buildingId, {
        type: meterFormData.type,
        name: meterFormData.name.trim() || undefined,
        unit: METER_TYPE_CONFIG[meterFormData.type].unit,
        meterNumber: meterFormData.meterNumber.trim() || undefined,
        consumptionPointNumber: meterFormData.consumptionPointNumber.trim() || undefined,
        energySupplier: meterFormData.energySupplier.trim() || undefined,
      });
      toast.success('Zähler angelegt');
      resetMeterForm();
      loadData();
    } catch { toast.error('Fehler beim Anlegen'); }
  };

  const handleOpenEditMeter = (m: Meter) => {
    setEditingMeter(m);
    setMeterEditData({
      name: m.name ?? '',
      meterNumber: m.meterNumber ?? '',
      consumptionPointNumber: m.consumptionPointNumber ?? '',
      energySupplier: m.energySupplier ?? '',
      currentReading: m.currentReading != null ? String(m.currentReading) : '',
    });
  };

  const handleUpdateMeter = async () => {
    if (!editingMeter) return;
    try {
      await buildingService.updateMeter(editingMeter.id, {
        name: meterEditData.name.trim() || undefined,
        meterNumber: meterEditData.meterNumber.trim() || undefined,
        consumptionPointNumber: meterEditData.consumptionPointNumber.trim() || undefined,
        energySupplier: meterEditData.energySupplier.trim() || undefined,
        currentReading: meterEditData.currentReading !== '' ? parseFloat(meterEditData.currentReading) : null,
      });
      toast.success('Zähler aktualisiert');
      setEditingMeter(null);
      loadData();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const handleDeleteMeter = async (meter: Meter) => {
    if (!window.confirm('Zähler löschen?')) return;
    try {
      await buildingService.deleteMeter(meter.id);
      toast.success('Gelöscht');
      loadData();
    } catch { toast.error('Fehler'); }
  };

  const handleOpenDeleteReadings = (m: Meter) => {
    setDeleteAlsoCurrentReading(false);
    setDeleteReadingsMeter(m);
  };

  const handleDeleteReadings = async () => {
    if (!deleteReadingsMeter) return;
    setDeletingReadings(true);
    try {
      const result = await energyService.deleteReadings(deleteReadingsMeter.id, deleteAlsoCurrentReading);
      toast.success(result.message);
      setDeleteReadingsMeter(null);
      loadData();
      if (selectedLogMeterId === deleteReadingsMeter.id) loadMeterLog(deleteReadingsMeter.id);
    } catch {
      toast.error('Fehler beim Löschen der Messwerte');
    } finally {
      setDeletingReadings(false);
    }
  };

  // Notes handler
  const handleCreateNote = async () => {
    if (!buildingId || !noteText.trim()) return;
    setSubmittingNote(true);
    try {
      const note = await buildingService.createNote(buildingId, { text: noteText.trim() });
      setNotes(prev => [...prev, note]);
      setNoteText('');
      toast.success('Notiz gespeichert');
    } catch { toast.error('Fehler beim Speichern der Notiz'); } finally { setSubmittingNote(false); }
  };

  const handleStartEditNote = (note: BuildingNote) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.text);
  };

  const handleUpdateNote = async () => {
    if (!editingNoteId || !editingNoteText.trim()) return;
    try {
      const updated = await buildingService.updateNote(editingNoteId, { text: editingNoteText.trim() });
      setNotes(prev => prev.map(n => n.id === editingNoteId ? updated : n));
      setEditingNoteId(null);
      setEditingNoteText('');
      toast.success('Notiz aktualisiert');
    } catch { toast.error('Fehler beim Aktualisieren der Notiz'); }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('Notiz löschen?')) return;
    try {
      await buildingService.deleteNote(noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success('Notiz gelöscht');
    } catch { toast.error('Fehler beim Löschen der Notiz'); }
  };

  // Document handlers
  const handleUploadDocument = async () => {
    if (!buildingId || !docFile || !docName.trim()) return;
    setUploadingDoc(true);
    try {
      const doc = await buildingService.uploadDocument(
        buildingId,
        docFile,
        docName.trim(),
        docCategory.trim() || undefined,
      );
      setDocuments(prev => [doc, ...prev]);
      setDocFile(null);
      setDocName('');
      setDocCategory('');
      setShowDocumentUpload(false);
      toast.success('Dokument hochgeladen');
    } catch { toast.error('Fehler beim Hochladen'); } finally { setUploadingDoc(false); }
  };

  const handleDeleteDocument = async (docId: number) => {
    if (!window.confirm('Dokument löschen?')) return;
    try {
      await buildingService.deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Dokument gelöscht');
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const handleDownloadDocument = async (doc: BuildingDocument) => {
    try {
      await buildingService.downloadDocument(doc.id, doc.name);
    } catch { toast.error('Fehler beim Herunterladen'); }
  };

  const handlePreviewDocument = async (doc: BuildingDocument) => {
    setPreviewLoading(true);
    try {
      const { blob } = await buildingService.fetchDocumentBlob(doc.id);
      const fileName = doc.fileName ?? doc.name;
      const url = URL.createObjectURL(blob);
      setPreviewDoc({ doc, url, fileName });
    } catch { toast.error('Vorschau konnte nicht geladen werden'); }
    finally { setPreviewLoading(false); }
  };

  const closePreview = () => {
    if (previewDoc) URL.revokeObjectURL(previewDoc.url);
    setPreviewDoc(null);
  };

  // Log helpers
  const formatLogTimestamp = (ts: string): string => {
    try {
      return new Date(ts).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    } catch { return ts; }
  };

  const formatConnectedSince = (ts?: string): string => {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return ts; }
  };

  const selectedMeter = meters.find((m) => m.id === selectedLogMeterId);
  const meterCurrentReading = selectedMeter?.currentReading ?? null;

  if (loading) return (
    <div className="flex items-center justify-center p-20">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#216dab]" />
    </div>
  );

  if (!building) return (
    <div className="text-center py-20">
      <p className="text-slate-500">Gebäude nicht gefunden.</p>
      <Link to="/buildings" className="text-[#216dab] hover:text-[#216dab] text-sm font-bold mt-4 inline-block">← Zurück zu Gebäude</Link>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <Link to="/buildings" className="text-sm font-medium text-[#216dab] hover:text-[#216dab] dark:text-[#3aa6e9] dark:hover:text-[#93cbf0] transition-colors inline-flex items-center gap-1 mb-3">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Gebäude
          </Link>
          <h1 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight transition-colors">{building.name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-slate-400 dark:text-slate-500 text-base font-normal transition-colors">{building.address || 'Keine Adresse'}</p>
            <span className="text-slate-300 dark:text-slate-600">·</span>
            <span className="text-sm font-medium text-slate-500">{meters.length} Zähler</span>
            {building.hasPv && (
              <>
                <span className="text-slate-300 dark:text-slate-600">·</span>
                <span className="text-sm font-medium text-amber-500 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 9H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                  PV
                </span>
              </>
            )}
          </div>
          {building.status && (
            <div className="mt-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-lg border text-sm font-medium ${STATUS_CONFIG[building.status]?.color ?? ''}`}>
                {STATUS_CONFIG[building.status]?.label ?? building.status}
                {building.statusChangedAt && (
                  <span className="ml-1.5 font-normal opacity-70">
                    · {new Date(building.statusChangedAt).toLocaleDateString('de-DE')}
                  </span>
                )}
              </span>
            </div>
          )}
          {(building.contactName || building.contactPhone || building.contactEmail) && (
            <div className="mt-2 flex flex-wrap items-center gap-4">
              {building.contactName && (
                <span className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  {building.contactName}
                </span>
              )}
              {building.contactPhone && (
                <a href={`tel:${building.contactPhone}`} className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-[#216dab] dark:hover:text-[#3aa6e9] transition-colors">
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  {building.contactPhone}
                </a>
              )}
              {building.contactEmail && (
                <a href={`mailto:${building.contactEmail}`} className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-[#216dab] dark:hover:text-[#3aa6e9] transition-colors">
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  {building.contactEmail}
                </a>
              )}
            </div>
          )}
        </div>
        {isAdmin() && (
          <button onClick={handleEditBuilding} className="px-5 py-2.5 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-600 dark:text-slate-300 rounded-2xl text-sm font-medium border border-black/5 dark:border-white/5 transition-all flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            Bearbeiten
          </button>
        )}
      </div>

      {/* Zähler-Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-slate-400">Zähler</h2>
          {isAdmin() && (
            <button onClick={() => { resetMeterForm(); setShowMeterForm(true); }} className="px-4 py-1.5 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-600 dark:text-slate-300 rounded-xl text-sm font-medium border border-black/5 dark:border-white/5 transition-all">
              + Neuer Zähler
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {meters.map(m => {
            const config = METER_TYPE_CONFIG[m.type];
            return (
              <div key={m.id} className="glass-card !bg-white dark:!bg-white/5 !rounded-2xl p-4 group/meter hover:shadow-lg transition-all">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-xl bg-black/5 dark:bg-white/5 shrink-0 ${config.color}`}>{config.icon}</div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{m.name || config.label}</p>
                      <p className="text-sm text-slate-500 font-semibold">{m.unit}</p>
                      {m.meterNumber && (
                        <p className="text-sm text-slate-400 mt-0.5">Nr. {m.meterNumber}</p>
                      )}
                      {m.energySupplier && (
                        <p className="text-sm text-slate-400">{m.energySupplier}</p>
                      )}
                      {m.currentReading != null && (
                        <p className="text-sm font-medium text-[#216dab] dark:text-[#3aa6e9] mt-1">
                          Zählerstand: {m.currentReading.toLocaleString('de-DE', { maximumFractionDigits: 3 })} {m.unit}
                        </p>
                      )}
                    </div>
                  </div>
                  {isAdmin() && (
                    <div className="flex items-center gap-1 opacity-0 group-hover/meter:opacity-100 transition-all shrink-0">
                      <button onClick={() => handleOpenEditMeter(m)} className="p-1.5 text-slate-400 hover:text-[#216dab] transition-colors rounded-lg hover:bg-[#216dab]/10">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                      <button onClick={() => handleDeleteMeter(m)} className="p-1.5 text-rose-500/60 hover:text-rose-500 transition-colors rounded-lg hover:bg-rose-500/10">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {meters.length === 0 && (
          <div className="text-center py-10 glass-card">
            <p className="text-sm text-slate-400 dark:text-slate-500 font-medium">Keine Zähler vorhanden.</p>
          </div>
        )}

        {/* Neuer Zähler Form */}
        {showMeterForm && (
          <div className="mt-6 glass-card p-6 border-[#216dab]/20 bg-[#216dab]/[0.03] animate-in fade-in zoom-in-95">
            <p className="text-sm font-medium text-slate-500 mb-4">Neuer Zähler</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-1">
                <label className={labelCls}>Typ</label>
                <select
                  value={meterFormData.type}
                  onChange={e => setMeterFormData({ ...meterFormData, type: e.target.value as MeterType })}
                  className={inputCls}
                >
                  {(Object.keys(METER_TYPE_CONFIG) as MeterType[]).map(t => (
                    <option key={t} value={t}>{METER_TYPE_CONFIG[t].label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className={labelCls}>Name (Optional)</label>
                <input type="text" value={meterFormData.name} onChange={e => setMeterFormData({ ...meterFormData, name: e.target.value })} className={inputCls} placeholder="z.B. Hauptzähler Keller" />
              </div>
              <div className="space-y-1">
                <label className={labelCls}>Zählernummer</label>
                <input type="text" value={meterFormData.meterNumber} onChange={e => setMeterFormData({ ...meterFormData, meterNumber: e.target.value })} className={inputCls} placeholder="z.B. 1EBZ0012345678" />
              </div>
              <div className="space-y-1">
                <label className={labelCls}>Verbrauchsstellennummer</label>
                <input type="text" value={meterFormData.consumptionPointNumber} onChange={e => setMeterFormData({ ...meterFormData, consumptionPointNumber: e.target.value })} className={inputCls} placeholder="z.B. DE00012345678" />
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className={labelCls}>Energielieferant</label>
                <input type="text" value={meterFormData.energySupplier} onChange={e => setMeterFormData({ ...meterFormData, energySupplier: e.target.value })} className={inputCls} placeholder="z.B. EnBW, E.ON, Stadtwerke..." />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={resetMeterForm} className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors">Abbrechen</button>
              <button onClick={handleCreateMeter} className="px-6 py-2 bg-[#216dab] text-white text-sm font-medium rounded-xl shadow-lg transition-all hover:bg-[#1a5a8f]">Zähler anlegen</button>
            </div>
          </div>
        )}
      </div>

      {/* Messwert-Protokoll */}
      <div className="glass-card overflow-hidden transition-all duration-500">
        <div className="p-6 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
          <h2 className="text-sm font-medium text-slate-500 transition-colors">Messwert-Protokoll</h2>
          {meterLog && (
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-400 leading-none mb-1">Verbunden seit</p>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{formatConnectedSince(meterLog.connectedSince)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-slate-400 leading-none mb-1">Gesamt</p>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{meterLog.totalEntries.toLocaleString('de-DE')} Messwerte</p>
              </div>
            </div>
          )}
        </div>
        <div className="p-6 space-y-5">
          {/* Meter selector */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-400 shrink-0">Zähler</label>
            <select
              value={selectedLogMeterId ?? ''}
              onChange={(e) => setSelectedLogMeterId(e.target.value === '' ? null : Number(e.target.value))}
              className="bg-white dark:bg-slate-900 border border-black/10 dark:border-white/10 text-sm font-medium text-slate-800 dark:text-slate-300 px-4 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 cursor-pointer transition-colors"
            >
              <option value="">— Zähler wählen —</option>
              {meters.map((m) => {
                const config = METER_TYPE_CONFIG[m.type];
                return (
                  <option key={m.id} value={m.id}>{m.name || config.label} ({m.unit})</option>
                );
              })}
            </select>
            {isAdmin() && selectedMeter && (
              <button
                onClick={() => handleOpenDeleteReadings(selectedMeter)}
                className="ml-auto shrink-0 px-4 py-2.5 text-sm font-medium text-rose-500 hover:text-white bg-rose-500/10 hover:bg-rose-500 border border-rose-500/20 rounded-xl transition-all inline-flex items-center gap-2"
                title="Alle Messwerte dieses Zählers löschen"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                Messwerte löschen
              </button>
            )}
          </div>

          {/* Log content */}
          {loadingLog ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#216dab]" />
            </div>
          ) : meterLog && meterLog.entries.length > 0 ? (
            <div className="rounded-2xl overflow-hidden border border-black/5 dark:border-white/5">
              <div className="overflow-y-auto max-h-96">
                <table className="w-full text-left">
                  <thead className="bg-black/[0.02] dark:bg-white/[0.02] sticky top-0">
                    <tr>
                      <th className="px-5 py-3 text-sm font-medium text-slate-400">Zeitstempel</th>
                      <th className="px-5 py-3 text-sm font-medium text-slate-500 text-right">Zählerstand</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5 dark:divide-white/5">
                    {meterLog.entries.map((entry, i) => {
                      const sumBefore = meterLog.entries.slice(0, i).reduce((acc, e) => acc + e.value, 0);
                      const standValue = meterCurrentReading != null ? meterCurrentReading - sumBefore : null;
                      return (
                        <tr key={i} className="hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors">
                          <td className="px-5 py-3 text-sm font-normal text-slate-600 dark:text-slate-400 whitespace-nowrap">
                            {formatLogTimestamp(entry.timestamp)}
                          </td>
                          <td className="px-5 py-3 text-right whitespace-nowrap">
                            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                              {standValue != null
                                ? standValue.toLocaleString('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
                                : '—'} {meterLog.unit}
                            </span>
                            <span className="ml-2 text-sm font-medium text-emerald-500 dark:text-emerald-400">
                              +{entry.value.toLocaleString('de-DE', { maximumFractionDigits: 3 })}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 bg-black/[0.01] dark:bg-white/[0.01] border-t border-black/5 dark:border-white/5">
                <p className="text-sm font-medium text-slate-400">
                  Letzte {meterLog.entries.length} von {meterLog.totalEntries.toLocaleString('de-DE')} Einträgen
                </p>
              </div>
            </div>
          ) : meterLog && meterLog.entries.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm text-slate-400 dark:text-slate-500 font-medium">Noch keine Messwerte für diesen Zähler vorhanden.</p>
            </div>
          ) : selectedLogMeterId === null ? (
            <div className="text-center py-10">
              <p className="text-sm text-slate-400 dark:text-slate-500 font-medium">Wähle einen Zähler aus, um die Log-Daten anzuzeigen.</p>
            </div>
          ) : null}
        </div>
      </div>

      {/* Notizen */}
      <div className="glass-card overflow-hidden">
        <div className="p-6 border-b border-black/5 dark:border-white/5">
          <h2 className="text-sm font-medium text-slate-500">Notizen</h2>
        </div>
        <div className="p-6 space-y-5">
          {/* Input */}
          <div className="space-y-2">
            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              rows={3}
              placeholder="Neue Notiz eingeben…"
              className={`${inputCls} resize-none`}
            />
            <div className="flex justify-end">
              <button
                onClick={handleCreateNote}
                disabled={submittingNote || !noteText.trim()}
                className="px-6 py-2 bg-[#216dab] text-white text-sm font-medium rounded-xl shadow-lg transition-all hover:bg-[#1a5a8f] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingNote ? 'Speichern…' : 'Notiz speichern'}
              </button>
            </div>
          </div>

          {/* Notes list */}
          {notes.length > 0 ? (
            <div className="space-y-3">
              {notes.map(note => (
                <div key={note.id} className="glass-card !bg-white dark:!bg-white/5 !rounded-2xl p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{note.authorName}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm text-slate-400 font-medium">
                        {new Date(note.createdAt).toLocaleString('de-DE', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                      {user?.id === note.userId && (
                        <button
                          onClick={() => handleStartEditNote(note)}
                          className="p-1 text-slate-400 hover:text-[#216dab] transition-colors rounded-lg hover:bg-[#216dab]/10"
                          title="Bearbeiten"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                      )}
                      {(user?.id === note.userId || isAdmin()) && (
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="p-1 text-rose-500/60 hover:text-rose-500 transition-colors rounded-lg hover:bg-rose-500/10"
                          title="Löschen"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      )}
                    </div>
                  </div>
                  {editingNoteId === note.id ? (
                    <div className="space-y-2 mt-2">
                      <textarea
                        value={editingNoteText}
                        onChange={e => setEditingNoteText(e.target.value)}
                        rows={3}
                        className={`${inputCls} resize-none`}
                        autoFocus
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }}
                          className="px-4 py-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
                        >Abbrechen</button>
                        <button
                          onClick={handleUpdateNote}
                          disabled={!editingNoteText.trim()}
                          className="px-5 py-1.5 bg-[#216dab] text-white text-sm font-medium rounded-xl shadow transition-all hover:bg-[#1a5a8f] disabled:opacity-50 disabled:cursor-not-allowed"
                        >Speichern</button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{note.text}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-slate-400 dark:text-slate-500 font-medium">Noch keine Notizen vorhanden.</p>
            </div>
          )}
        </div>
      </div>

      {/* Dokumente */}
      <div className="glass-card p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-black text-slate-800 dark:text-white tracking-tight">Dokumente</h2>
          <button
            onClick={() => setShowDocumentUpload(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#216dab] text-white text-sm font-bold rounded-xl shadow transition-all hover:bg-[#1a5a8f]"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            Hochladen
          </button>
        </div>

        {/* Upload form */}
        {showDocumentUpload && (
          <div className="mb-6 p-5 bg-black/5 dark:bg-white/5 rounded-2xl space-y-4 border border-black/5 dark:border-white/5">
            <div className="space-y-1">
              <label className={labelCls}>Datei</label>
              <input
                type="file"
                onChange={e => {
                  const f = e.target.files?.[0] ?? null;
                  setDocFile(f);
                  if (f && !docName) setDocName(f.name.replace(/\.[^.]+$/, ''));
                }}
                className="block w-full text-sm text-slate-600 dark:text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-[#216dab] file:text-white hover:file:bg-[#1a5a8f] file:cursor-pointer"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className={labelCls}>Name</label>
                <input
                  type="text"
                  value={docName}
                  onChange={e => setDocName(e.target.value)}
                  className={inputCls}
                  placeholder="z.B. Wartungsvertrag 2024"
                />
              </div>
              <div className="space-y-1">
                <label className={labelCls}>Kategorie (optional)</label>
                <input
                  type="text"
                  value={docCategory}
                  onChange={e => setDocCategory(e.target.value)}
                  className={inputCls}
                  placeholder="z.B. Vertrag, Rechnung, Bericht"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDocumentUpload(false); setDocFile(null); setDocName(''); setDocCategory(''); }}
                className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
              >Abbrechen</button>
              <button
                onClick={handleUploadDocument}
                disabled={!docFile || !docName.trim() || uploadingDoc}
                className="px-5 py-2 bg-[#216dab] text-white text-sm font-bold rounded-xl shadow transition-all hover:bg-[#1a5a8f] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingDoc ? 'Wird hochgeladen…' : 'Hochladen'}
              </button>
            </div>
          </div>
        )}

        {/* Document list */}
        {documents.length > 0 ? (
          <div className="space-y-2">
            {documents.map(doc => (
              <div key={doc.id} className="glass-card !bg-white dark:!bg-white/5 !rounded-2xl p-4 flex items-center gap-4">
                <div className="p-2.5 rounded-xl bg-[#216dab]/10 text-[#216dab] shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">{doc.name}</p>
                  <p className="text-xs text-slate-400 font-medium mt-0.5">
                    {doc.category && <span className="mr-2 text-[#216dab]">{doc.category}</span>}
                    {doc.uploaderName} · {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString('de-DE') : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handlePreviewDocument(doc)}
                    disabled={previewLoading}
                    className="p-2 text-slate-400 hover:text-[#216dab] transition-colors rounded-xl hover:bg-[#216dab]/10 disabled:opacity-40"
                    title="Vorschau"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  </button>
                  <button
                    onClick={() => handleDownloadDocument(doc)}
                    className="p-2 text-slate-400 hover:text-[#216dab] transition-colors rounded-xl hover:bg-[#216dab]/10"
                    title="Herunterladen"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  </button>
                  {isAdmin() && (
                    <button
                      onClick={() => handleDeleteDocument(doc.id)}
                      className="p-2 text-rose-500/60 hover:text-rose-500 transition-colors rounded-xl hover:bg-rose-500/10"
                      title="Löschen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-sm text-slate-400 dark:text-slate-500 font-medium">Noch keine Dokumente hochgeladen.</p>
          </div>
        )}
      </div>

      {/* Dokument Vorschau Modal */}
      {previewDoc && (() => {
        const ext = previewDoc.fileName.split('.').pop()?.toLowerCase() ?? '';
        const isPdf = ext === 'pdf';
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
        return (
          <div className="fixed inset-0 z-50 flex flex-col">
            <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={closePreview} />
            <div className="relative z-10 flex flex-col h-full max-w-5xl w-full mx-auto my-6 px-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-white font-semibold truncate max-w-[calc(100%-3rem)]">{previewDoc.doc.name}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleDownloadDocument(previewDoc.doc)}
                    className="p-2 text-white/70 hover:text-white transition-colors rounded-xl hover:bg-white/10"
                    title="Herunterladen"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  </button>
                  <button onClick={closePreview} className="p-2 text-white/70 hover:text-white transition-colors rounded-xl hover:bg-white/10">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>
              {/* Content */}
              <div className="flex-1 rounded-2xl overflow-hidden bg-white dark:bg-slate-900 min-h-0">
                {isPdf && (
                  <PdfPreview url={previewDoc.url} />
                )}
                {isImage && (
                  <div className="w-full h-full flex items-center justify-center p-4">
                    <img src={previewDoc.url} alt={previewDoc.doc.name} className="max-w-full max-h-full object-contain rounded-xl" />
                  </div>
                )}
                {!isPdf && !isImage && (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-slate-500 dark:text-slate-400">
                    <svg className="w-16 h-16 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    <p className="text-sm font-medium">Keine Vorschau verfügbar für .{ext}-Dateien</p>
                    <button onClick={() => handleDownloadDocument(previewDoc.doc)} className="px-4 py-2 bg-[#216dab] text-white text-sm font-bold rounded-xl hover:bg-[#1a5a8f] transition-colors">
                      Herunterladen
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Gebäude bearbeiten Modal */}
      {showBuildingEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40 dark:bg-slate-950/60 backdrop-blur-sm" onClick={() => setShowBuildingEdit(false)} />
          <div className="glass-card w-full max-w-lg relative z-10 p-8 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight mb-8">Gebäude bearbeiten</h2>
            <form onSubmit={handleUpdateBuilding} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-500 px-1">Name</label>
                <input type="text" required value={buildingFormData.name} onChange={(e) => setBuildingFormData({ ...buildingFormData, name: e.target.value })} className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-500 px-1">Adresse</label>
                <input type="text" value={buildingFormData.address} onChange={(e) => setBuildingFormData({ ...buildingFormData, address: e.target.value })} className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all" />
              </div>
              <div className="flex items-center gap-6 pt-1">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`w-10 h-6 rounded-full relative transition-colors ${buildingFormData.hasPv ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-700'}`}>
                    <input type="checkbox" className="hidden" checked={buildingFormData.hasPv} onChange={(e) => setBuildingFormData({ ...buildingFormData, hasPv: e.target.checked })} />
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${buildingFormData.hasPv ? 'left-5' : 'left-1'}`} />
                  </div>
                  <span className="text-sm font-medium text-slate-500 dark:text-slate-400">PV-Anlage</span>
                </label>
              </div>
              {/* Ansprechpartner */}
              <div className="pt-2 border-t border-black/5 dark:border-white/5">
                <p className="text-sm font-medium text-slate-500 px-1 mb-3">Ansprechpartner (Optional)</p>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-400 px-1">Name</label>
                    <input type="text" value={buildingFormData.contactName} onChange={(e) => setBuildingFormData({ ...buildingFormData, contactName: e.target.value })} className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all" placeholder="z.B. Max Mustermann" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-slate-400 px-1">Telefon</label>
                      <input type="tel" inputMode="numeric" value={buildingFormData.contactPhone} onChange={(e) => setBuildingFormData({ ...buildingFormData, contactPhone: e.target.value.replace(/\D/g, '') })} className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all" placeholder="030 12345678" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-slate-400 px-1">E-Mail</label>
                      <input type="email" value={buildingFormData.contactEmail} onChange={(e) => setBuildingFormData({ ...buildingFormData, contactEmail: e.target.value })} className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all" placeholder="kontakt@beispiel.de" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Projektstatus */}
              <div className="pt-2 border-t border-black/5 dark:border-white/5">
                <p className="text-sm font-medium text-slate-500 px-1 mb-3">Projektstatus</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-400 px-1">Status</label>
                    <select
                      value={buildingFormData.status}
                      onChange={(e) => setBuildingFormData({ ...buildingFormData, status: e.target.value as BuildingStatus })}
                      className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all"
                    >
                      {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-400 px-1">Datum</label>
                    <input
                      type="date"
                      value={buildingFormData.statusChangedAt}
                      onChange={(e) => setBuildingFormData({ ...buildingFormData, statusChangedAt: e.target.value })}
                      className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#216dab]/50 transition-all"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowBuildingEdit(false)} className="flex-1 px-6 py-3 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-500 rounded-2xl font-bold transition-all">Abbrechen</button>
                <button type="submit" className="flex-1 px-6 py-3 bg-[#216dab] dark:bg-[#216dab] text-white rounded-2xl font-bold shadow-lg transition-all">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Messwerte löschen Modal */}
      {deleteReadingsMeter && (() => {
        const config = METER_TYPE_CONFIG[deleteReadingsMeter.type];
        const entryCount = meterLog && meterLog.meterId === deleteReadingsMeter.id ? meterLog.totalEntries : null;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-slate-950/40 dark:bg-slate-950/60 backdrop-blur-sm" onClick={() => !deletingReadings && setDeleteReadingsMeter(null)} />
            <div className="glass-card w-full max-w-lg relative z-10 p-8 animate-in zoom-in-95 duration-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-xl bg-rose-500/10 text-rose-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                </div>
                <div>
                  <h2 className="text-xl font-black text-slate-800 dark:text-white tracking-tight">Messwerte löschen</h2>
                  <p className="text-sm text-slate-500 font-semibold tracking-widest">{deleteReadingsMeter.name || config.label} · {deleteReadingsMeter.unit}</p>
                </div>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-300">
                {entryCount != null
                  ? <>Alle <span className="font-bold">{entryCount.toLocaleString('de-DE')}</span> Messwerte dieses Zählers werden gelöscht.</>
                  : <>Alle Messwerte dieses Zählers werden gelöscht.</>}
                {' '}Das lässt sich nicht rückgängig machen.
              </p>

              <label className="mt-5 flex items-start gap-3 p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteAlsoCurrentReading}
                  onChange={e => setDeleteAlsoCurrentReading(e.target.checked)}
                  className="mt-0.5 w-4 h-4 shrink-0 accent-rose-500 cursor-pointer"
                />
                <span>
                  <span className="block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Zählerstand ebenfalls löschen
                    {deleteReadingsMeter.currentReading != null && (
                      <span className="ml-1.5 font-medium text-slate-500">
                        (aktuell {deleteReadingsMeter.currentReading.toLocaleString('de-DE', { maximumFractionDigits: 3 })} {deleteReadingsMeter.unit})
                      </span>
                    )}
                  </span>
                  <span className="block text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Setzt auch die Delta-Baseline der angebundenen MQTT-Kanäle zurück — der nächste
                    eingehende Wert pegelt sie neu ein und bucht selbst noch keinen Verbrauch.
                    Ohne Haken bleibt der Zählerstand stehen und die Messung läuft lückenlos weiter.
                  </span>
                </span>
              </label>

              <div className="flex gap-4 pt-6">
                <button
                  onClick={() => setDeleteReadingsMeter(null)}
                  disabled={deletingReadings}
                  className="flex-1 px-6 py-3 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-500 rounded-2xl font-bold transition-all disabled:opacity-50"
                >Abbrechen</button>
                <button
                  onClick={handleDeleteReadings}
                  disabled={deletingReadings}
                  className="flex-1 px-6 py-3 bg-rose-500 text-white rounded-2xl font-bold shadow-lg transition-all hover:bg-rose-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >{deletingReadings ? 'Wird gelöscht…' : 'Endgültig löschen'}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Zähler bearbeiten Modal */}
      {editingMeter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/40 dark:bg-slate-950/60 backdrop-blur-sm" onClick={() => setEditingMeter(null)} />
          <div className="glass-card w-full max-w-lg relative z-10 p-8 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-6">
              <div className={`p-2.5 rounded-xl bg-black/5 dark:bg-white/5 ${METER_TYPE_CONFIG[editingMeter.type].color}`}>
                {METER_TYPE_CONFIG[editingMeter.type].icon}
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-800 dark:text-white tracking-tight">Zähler bearbeiten</h2>
                <p className="text-sm text-slate-500 font-semibold tracking-widest">{METER_TYPE_CONFIG[editingMeter.type].label} · {editingMeter.unit}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className={labelCls}>Name</label>
                <input type="text" value={meterEditData.name} onChange={e => setMeterEditData({ ...meterEditData, name: e.target.value })} className={inputCls} placeholder={METER_TYPE_CONFIG[editingMeter.type].label} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className={labelCls}>Zählernummer</label>
                  <input type="text" value={meterEditData.meterNumber} onChange={e => setMeterEditData({ ...meterEditData, meterNumber: e.target.value })} className={inputCls} placeholder="z.B. 1EBZ0012345678" />
                </div>
                <div className="space-y-1">
                  <label className={labelCls}>Verbrauchsstellennummer</label>
                  <input type="text" value={meterEditData.consumptionPointNumber} onChange={e => setMeterEditData({ ...meterEditData, consumptionPointNumber: e.target.value })} className={inputCls} placeholder="z.B. DE00012345678" />
                </div>
              </div>
              <div className="space-y-1">
                <label className={labelCls}>Energielieferant</label>
                <input type="text" value={meterEditData.energySupplier} onChange={e => setMeterEditData({ ...meterEditData, energySupplier: e.target.value })} className={inputCls} placeholder="z.B. EnBW, E.ON, Stadtwerke..." />
              </div>
              <div className="space-y-1">
                <label className={labelCls}>Zählerstand ({editingMeter.unit})</label>
                <input
                  type="number"
                  step="0.001"
                  value={meterEditData.currentReading}
                  onChange={e => setMeterEditData({ ...meterEditData, currentReading: e.target.value })}
                  className={inputCls}
                  placeholder="Aktueller Zählerstand"
                />
                <p className="text-sm text-slate-400 px-1 mt-0.5">Wird automatisch durch eingehende MQTT-Messwerte aktualisiert</p>
              </div>
            </div>
            <div className="flex gap-4 pt-6">
              <button onClick={() => setEditingMeter(null)} className="flex-1 px-6 py-3 bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-slate-500 rounded-2xl font-bold transition-all">Abbrechen</button>
              <button onClick={handleUpdateMeter} className="flex-1 px-6 py-3 bg-[#216dab] dark:bg-[#216dab] text-white rounded-2xl font-bold shadow-lg transition-all hover:bg-[#1a5a8f]">Speichern</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildingDetail;
